import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from 'react'

import styled from 'styled-components'
import Joi from 'joi'
import toast from 'react-hot-toast'

import {
  SingleSelect,
  MultiSelect,
  CreatableSelectComponent,
} from '../../../components/Select'

import apiClient from '../../../helpers/apiClient'

import { getFromLocalStorage } from '../../../utils/util'

const Qualifications = forwardRef((props, ref) => {
  const [specialization, setSpecialization] = useState([])
  const [techSkills, setTechSkills] = useState([])
  const [personalSkills, setPersonalSkills] = useState([])
  const [hardwareRequirements, setHardwareRequirements] = useState([])
  const [years, setYears] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})

  const [yearList, setYearList] = useState()
  const [personalSkillList, setPersonalSkillList] = useState([])
  const [techSkillList, setTechSkillList] = useState([])

  const validationSchema = useMemo(() => {
    return Joi.object({
      years: Joi.string().label('Years of Experience').required(),
      specialization: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Specialization')
        .required()
        .messages({
          'array.min': 'Please add at least one specialization.',
          'any.required': 'Specialization is required.',
        }),
      techSkills: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Technical Skills')
        .required()
        .messages({
          'array.min': 'Please select at least one technical skill.',
          'any.required': 'Technical Skill selection is required.',
        }),
      personalSkills: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Personal Skills')
        .required()
        .messages({
          'array.min': 'Please select at least one personal skill.',
          'any.required': 'Personal Skill selection is required.',
        }),
      hardwareRequirements: Joi.array()
        .items(
          Joi.object({
            value: Joi.string().required(),
            label: Joi.string().required(),
          })
        )
        .min(1)
        .label('Hardware Requirements')
        .required()
        .messages({
          'array.min': 'Please add at least one hardware requirement.',
          'any.required': 'Hardware Requirement is required.',
        }),
    })
  }, [])

  useEffect(() => {
    const qualifications = getFromLocalStorage('qualifications')

    setYears(qualifications?.years || '')
    setSpecialization(
      (qualifications && JSON.parse(qualifications?.specialization)) || []
    )
    setTechSkills(
      (qualifications && JSON.parse(qualifications?.techSkills)) || []
    )
    setHardwareRequirements(
      (qualifications && JSON.parse(qualifications?.hardwareRequirements)) || []
    )
    setPersonalSkills(
      (qualifications && JSON.parse(qualifications?.personalSkills)) || []
    )

    apiClient.get('/technical-skills?all=true').then((resp) => {
      const result = resp?.data?.resp
      const mappedResult = result.map((e) => ({
        value: e.skills,
        label: e.skills,
      }))
      setTechSkillList(mappedResult)
    })
    apiClient.get('/skills').then((resp) => {
      const result = resp?.data?.data?.softSkills
      const mappedResult = result.map((e) => ({
        value: e,
        label: e,
      }))
      setPersonalSkillList(mappedResult)
    })

    setYearList([
      { value: '1 Year', label: '1 Year' },
      {
        value: '2 Years',
        label: '2 Years',
      },
      {
        value: '3 Years',
        label: '3 Years',
      },
      {
        value: '4 Years',
        label: '4 Years',
      },
      {
        value: '5 Years',
        label: '5 Years',
      },
      {
        value: 'More than 5 Years',
        label: 'More than 5 Years',
      },
    ])
  }, [])

  const handleNext = () => {
    const formData = {
      years,
      specialization: JSON.stringify(specialization),
      techSkills: JSON.stringify(techSkills),
      hardwareRequirements: JSON.stringify(hardwareRequirements),
      personalSkills: JSON.stringify(personalSkills),
    }

    const forValidation = {
      years: years.label,
      specialization,
      techSkills,
      hardwareRequirements,
      personalSkills,
    }

    const result = validationSchema.validate(forValidation, {
      abortEarly: false,
    })

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.context.key] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      setFieldErrors(validationErrors)

      toast.error('Please fix the validation errors to continue.')

      return false
    }

    localStorage.setItem('qualifications', JSON.stringify(formData))

    return true
  }

  useImperativeHandle(ref, () => ({
    onNext: handleNext,
  }))

  return (
    <StyledQualifications>
      <SingleSelect
        title="Years of Experience"
        options={yearList}
        selected={years}
        placeholder="Select Years of Experience"
        onChange={(e) => setYears(e)}
        wrapperClass={fieldErrors.years && 'error'}
        subText={fieldErrors.years}
      />

      <CreatableSelectComponent
        noOption
        title="Specialization"
        id="hardwareRequirements"
        selected={specialization}
        onChange={setSpecialization}
        placeholder="Type something and press enter..."
        wrapperClass={fieldErrors.specialization && 'error'}
        subText={fieldErrors.specialization}
      />

      <MultiSelect
        title="Technical Skills"
        options={techSkillList}
        selected={techSkills}
        placeholder="Select Technical Skills"
        onChange={(e) => setTechSkills(e)}
        wrapperClass={fieldErrors.techSkills && 'error'}
        subText={fieldErrors.techSkills}
      />

      <CreatableSelectComponent
        noOption
        title="Hardware Requirements"
        id="hardwareRequirements"
        selected={hardwareRequirements}
        onChange={setHardwareRequirements}
        placeholder="Type something and press enter..."
        wrapperClass={fieldErrors.hardwareRequirements && 'error'}
        subText={fieldErrors.hardwareRequirements}
      />

      <MultiSelect
        title="Personal Skills"
        options={personalSkillList}
        selected={personalSkills}
        placeholder="Select Personal Skill"
        onChange={(e) => setPersonalSkills(e)}
        wrapperClass={fieldErrors.personalSkills && 'error'}
        subText={fieldErrors.personalSkills}
      />
    </StyledQualifications>
  )
})

const StyledQualifications = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  width: 100%;
`

Qualifications.displayName = 'Qualifications'

export default Qualifications
